import * as React from 'react';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Unstable_Grid2'; // Grid version 2
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {useLoaderData} from 'react-router-dom';
import {useMessage} from '../context/MessageProvider';
import {useAuth} from '../context/AuthProvider';

import {useForm, FormProvider} from 'react-hook-form';
import Form from '../components/form/Form';

import FormTextField from '../components/form/FormTextField';
import FormNumberTextField from '../components/form/FormNumberTextField';
import FormButton from '../components/form/FormButton';
import FormDropDown from '../components/form/FormDropDown';
import FormObjectArrayList from '../components/form/FormObjectArrayList';
import FormColorPicker from '../components/form/FormColorPicker';
import FormFileUpload from '../components/form/FormFileUpload';

import Header from "../components/Header";
import Subheader from "../components/Subheader";

import { getFacility, saveFacility, uploadFacilityLogo } from '../models/facility';
import { reviewPeriods } from '../models/categories';

import { useConnectivity } from '../context/ConnectivityProvider';

export async function loader() {
    //return {facility: {name: 'Franks',poc_name:'',poc_email: '', poc_phone: '', review_period: '', use_24_hour_clock: true, start_of_day: time(), end_of_day: time('15:30')}};
    return {
        facility: await getFacility(),
        reviewPeriods: await reviewPeriods()
    };
}


export default function InstitutionDetailsFormPage() {
  const {setSuccess, setError} = useMessage();
  const {isOnline} = useConnectivity();

  const {updateFacility} = useAuth();

  const {facility, reviewPeriods} = useLoaderData();
  const [working, setWorking] = React.useState(false);
  const [logoFile, setLogoFile] = React.useState(null);
  const [logoPreview, setLogoPreview] = React.useState(facility?.logo_url || null);

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const onSubmit = async (data) => {
    try {
        setWorking(true);
        const primaryTimeframe = (data.workday_timeframes || []).find((cur)=>cur?.start_of_day || cur?.end_of_day) || data.workday_timeframes?.[0];
        if (primaryTimeframe) {
            data.start_of_day = primaryTimeframe.start_of_day;
            data.end_of_day = primaryTimeframe.end_of_day;
        }
        if (logoFile) {
            data.logo_url = await uploadFacilityLogo(logoFile, facility.id);
            setLogoFile(null);
        }
        await saveFacility(data, isOnline);
        updateFacility(data);
        setSuccess("Institution Saved");

    } catch (error) {
        console.error(error);
        setError(error);
    } finally {
        setWorking(false);
    }
  }

    console.log("facility:", facility);

  const timeframesDefault = (facility?.workday_timeframes?.length)
    ? facility.workday_timeframes
    : (facility?.start_of_day && facility?.end_of_day)
      ? [{start_of_day: facility.start_of_day, end_of_day: facility.end_of_day}]
      : [{start_of_day: null, end_of_day: null}];

	const defaultValues = {...facility, workday_timeframes: timeframesDefault};
	const methods = useForm({ defaultValues });

  return (
    <Container component="main" maxWidth="sm">
    <Header label="Institution Details" />
    <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmit)}>
            <Grid container spacing={2}>
                <Grid xs={12}>
                    <FormTextField name="name" label="Name of Institution" required/>
                </Grid>

                <Grid xs={12}>
                    <FormTextField name="poc_name" label="Point of Contact Name" required/>
                </Grid>

                <Grid xs={12}>
                    <FormTextField name="poc_email" label="Point of Contact Email" required/>
                </Grid>

                <Grid xs={12}>
                    <FormTextField name="poc_phone" label="Point of Contact Phone"  required/>
                </Grid>

                <Grid xs={12}>
                    <FormDropDown name="review_period" label="Review Period" list={reviewPeriods} required/>
                </Grid>

                <Grid xs={12}>
                    <Subheader label="Work Day Hours" />
                </Grid>

                <Grid xs={12}>
                    <FormObjectArrayList
                        name="workday_timeframes"
                        label="Workday Timeframes"
                        showRowIndex={false}
                        buttonText="Add Timeframe"
                        newRow={() => ({start_of_day: null, end_of_day: null})}
                        fields={[
                            {
                                key: 'start_of_day',
                                label: 'Start Of Day',
                                type: 'time',
                                componentProps: {required: true},
                                gridProps: {xs: 12, sm: 6},
                            },
                            {
                                key: 'end_of_day',
                                label: 'End Of Day',
                                type: 'time',
                                componentProps: {required: true},
                                gridProps: {xs: 12, sm: 6},
                            },
                        ]}
                    />
                </Grid>

                <Grid xs={12} sm={6}>
                    <FormNumberTextField name="section_a_observations_total" label="Section A Observations Total" required min={4} max={12} />
                </Grid>

                <Grid xs={12} sm={6}>
                    <FormNumberTextField name="section_b_observations_total" label="Section B Observations Total" required min={4} max={12}/>
                </Grid>


                <Grid xs={12}>
                    <Subheader label="Branding" />
                </Grid>

                <Grid xs={12} sm={6}>
                    <FormColorPicker name="primary_color" label="Primary Colour" />
                </Grid>

                <Grid xs={12} sm={6}>
                    <FormColorPicker name="secondary_color" label="Secondary Colour" />
                </Grid>

                <Grid xs={12}>
                    <FormFileUpload
                        label="Facility Logo"
                        buttonLabel="Choose Logo"
                        accept="image/png,image/jpeg,image/svg+xml"
                        file={logoFile}
                        onChange={handleLogoChange}
                    />
                    {logoPreview && (
                        <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                                component="img"
                                src={logoPreview}
                                alt="Logo preview"
                                sx={{ height: 48, objectFit: 'contain' }}
                            />
                            <Typography variant="caption" color="text.secondary">
                                {logoFile ? 'New logo (not yet saved)' : 'Current logo'}
                            </Typography>
                        </Box>
                    )}
                </Grid>

                <Grid xs={12}>
                    <FormButton working={working}>Submit</FormButton>
                </Grid>
            </Grid>
        </Form>
    </FormProvider>
    </Container>
  );
}
